import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import { Field, InlineError, PageHeader, Panel, Spinner, TextInput } from '../components/ui.js';

export function ChangePasswordPage() {
  const { t } = useI18n();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setFields({});
    setSubmitting(true);

    try {
      await api('/auth/password', {
        method: 'POST',
        body: { currentPassword, newPassword, confirmPassword },
      });
      // Changing a password revokes every session, including this one, so the
      // user is sent back to sign in rather than left on a dead session.
      await signOut();
      navigate('/login', { replace: true, state: { message: t('password.changed') } });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        setError(caught.message);
        setFields(caught.fields);
      } else {
        setError(t('common.error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl">
      <PageHeader
        title={t('password.title')}
        subtitle={user?.mustChangePassword ? t('password.required') : undefined}
      />

      <Panel bodyClassName="px-5 py-5">
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {error && <InlineError message={error} />}

          <Field
            label={t('password.current')}
            htmlFor="current"
            error={fields['currentPassword']}
            required
          >
            <TextInput
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
            />
          </Field>

          <Field
            label={t('password.new')}
            htmlFor="next"
            error={fields['newPassword']}
            hint={t('password.hint')}
            required
          >
            <TextInput
              id="next"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
          </Field>

          <Field
            label={t('password.confirm')}
            htmlFor="confirm"
            error={fields['confirmPassword']}
            required
          >
            <TextInput
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
            />
          </Field>

          <div className="flex justify-end gap-2 pt-1">
            {!user?.mustChangePassword && (
              <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>
                {t('action.cancel')}
              </button>
            )}
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting && <Spinner />}
              {t('action.save')}
            </button>
          </div>
        </form>
      </Panel>
    </div>
  );
}
